/**
 * Shared utility functions for status colors and common operations
 */

import { formatCurrency as formatCurrencyCanonical } from "@west-tennessee-consulting/ui";

/**
 * Get priority color classes
 */
export function getPriorityColor(priority: string) {
  const colors = {
    critical: "bg-red-100 text-red-800 border-red-200",
    urgent: "bg-orange-100 text-orange-800 border-orange-200",
    high: "bg-yellow-100 text-yellow-800 border-yellow-200",
    medium: "bg-blue-100 text-blue-800 border-blue-200",
    low: "bg-card-100 text-gray-800 border-gray-200",
  };
  return colors[priority as keyof typeof colors] || colors.low;
}

/**
 * Get status color classes
 */
export function getStatusColor(status: string) {
  const colors = {
    new: "bg-blue-100 text-blue-800",
    in_progress: "bg-yellow-100 text-yellow-800",
    waiting_for_vendor: "bg-purple-100 text-purple-800",
    resolved: "bg-green-100 text-green-800",
    closed: "bg-card-100 text-gray-800",
  };
  return colors[status as keyof typeof colors] || colors.new;
}

/**
 * Get activity icon component
 */
export function getActivityIcon(type: string) {
  const icons = {
    status_change: "MessageSquare",
    time_entry: "Timer",
    ticket_created: "Plus",
    ticket_update: "Edit",
    comment: "MessageSquare",
  };
  return icons[type as keyof typeof icons] || "MessageSquare";
}

/**
 * Get activity color classes
 */
export function getActivityColor(type: string) {
  const colors = {
    status_change: "bg-blue-100 text-blue-800",
    time_entry: "bg-green-100 text-green-800",
    ticket_created: "bg-purple-100 text-purple-800",
    ticket_update: "bg-orange-100 text-orange-800",
    comment: "bg-card-100 text-gray-800",
  };
  return colors[type as keyof typeof colors] || "bg-card-100 text-gray-800";
}

/**
 * Format timestamp for display
 */
export function formatTimestamp(timestamp: string) {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

  if (diffInMinutes < 1) return "Just now";
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
  return date.toLocaleDateString();
}

/**
 * Format currency for display.
 *
 * Delegates to the canonical `@wtc/ui` formatter while preserving this helper's
 * configurable-currency call sites and the prior Intl default of 2 fixed
 * fraction digits. Call sites render the result in JSX, so a non-null string is
 * required — coalesce to "".
 */
export function formatCurrency(amount: number, currency = "USD"): string {
  return (
    formatCurrencyCanonical(amount, {
      currency,
      fallback: "",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) ?? ""
  );
}

/**
 * Format hours for display
 */
export function formatHours(hours: number) {
  if (hours === 0) return "0h";
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  return `${hours.toFixed(1)}h`;
}

/**
 * Get percentage color based on value
 */
export function getPercentageColor(percentage: number) {
  if (percentage >= 100) return "text-green-600";
  if (percentage >= 80) return "text-blue-600";
  if (percentage >= 60) return "text-yellow-600";
  return "text-red-600";
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(text: string, maxLength: number) {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
}

/**
 * Capitalize first letter of each word
 */
export function capitalizeWords(text: string) {
  return text.replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Convert snake_case to Title Case
 */
export function snakeToTitleCase(text: string) {
  return text.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}
